import type { ComponentChildren } from 'preact'
import { useId } from 'preact/hooks'
import type { SendingDomainHeldBroadcastDto, SendingDomainStoppedDto, SendingDomainViewDto, SendingDomainWarmupViewDto } from '@/types/sendingDomains'
import { routes } from '@/utils/routes'
import { formatCount, localShortDay } from '@/utils/sendingDomainWarmup'
import {
  WHILE_STOPPED_ROWS,
  afterResumeSentence,
  broadcastEmailCount,
  checkFirstRuns,
  heldBroadcastSource,
  heldGoOutSentence,
  limitWhenStoppedPhrase,
  regionLabel,
  resumeRestartLine,
  stoppedVerdict,
  type WhileStoppedRow,
} from '@/utils/sendingDomainStatus'
import { BlockHeading, Chip, CopyRuns, DomainStatusBox, WarmupLoading, type StatusFact } from './DomainPageParts'
import { DomainStatePill } from './DomainStatePill'
import { LastThreeDays } from './LastThreeDays'
import { WarmupLadder } from './WarmupLadder'
import { PRIMARY_BUTTON, SECONDARY_BUTTON, TEXT_LINK } from './styles'

const EFFECT_TONES: Record<WhileStoppedRow['effect'], 'green' | 'slate' | 'red'> = {
  stillSending: 'green',
  off: 'slate',
  cancelledIfLate: 'red',
}

interface DomainStoppedViewProps {
  clientCompanyId: string
  domain: SendingDomainViewDto
  warmup: SendingDomainWarmupViewDto | null
  warmupError: string | null
  busy: boolean
  onResume: () => void
  onRestart: () => void
}

/** A domain the automatic stop caught: what is held, what still sends, what to check, and how to resume. */
export function DomainStoppedView({ clientCompanyId, domain, warmup, warmupError, busy, onResume, onRestart }: DomainStoppedViewProps) {
  const headingId = useId()
  if (!warmup) return <WarmupLoading error={warmupError} />
  const stopped = warmup.Stopped
  if (!stopped) {
    console.error('Stopped sending domain has no stop details', domain.Id)
    return null
  }

  const facts: StatusFact[] = [
    { label: 'Status', value: <DomainStatePill domain={domain} /> },
    { label: 'Region', value: regionLabel(domain.Region) },
  ]
  if (domain.StoppedAtUtc) facts.push({ label: 'Stopped on', value: localShortDay(domain.StoppedAtUtc) })
  facts.push({ label: 'Limit when stopped', value: limitWhenStoppedPhrase(domain) })

  const checkFirst = checkFirstRuns(domain, stopped)

  return (
    <div className="space-y-10">
      <DomainStatusBox pill="Stopped" verdict={stoppedVerdict(domain, stopped.HeldEmails, warmup.RateLimits)} facts={facts} />

      <section aria-labelledby={headingId} className="space-y-5">
        <h3 id={headingId} className="text-base font-semibold text-slate-900 dark:text-white">While it's stopped</h3>

        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
          <HeldRow clientCompanyId={clientCompanyId} stopped={stopped} />
          {WHILE_STOPPED_ROWS.map(row => (
            <StoppedRow key={row.effect} tag={<Chip tone={EFFECT_TONES[row.effect]}>{row.tag}</Chip>} title={row.title}>{row.text}</StoppedRow>
          ))}
        </ul>

        {checkFirst && (
          <p className="max-w-prose text-sm text-slate-700 dark:text-slate-300">
            <CopyRuns runs={checkFirst} strongClassName="font-semibold text-slate-900 dark:text-white" />
          </p>
        )}

        <div className="space-y-2.5">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onResume} disabled={busy} className={PRIMARY_BUTTON}>
              Resume at {formatCount(stopped.ResumeDailyLimit)} a day
            </button>
            <button type="button" onClick={onRestart} disabled={busy} className={SECONDARY_BUTTON}>Restart warm-up</button>
          </div>
          <p className="max-w-prose text-[13px] text-slate-600 dark:text-slate-400">
            {resumeRestartLine(stopped, domain.DailyLimit ?? null, warmup.Steps, { saysAdminsOnly: true })}
          </p>
        </div>

        <div className="space-y-3 pt-3">
          <BlockHeading>Limits by step</BlockHeading>
          <WarmupLadder
            steps={warmup.Steps}
            position={{ kind: 'stopped', stoppedAtStep: stopped.StoppedAtStep ?? null, resumeStep: stopped.ResumeStep }}
          />
          <p className="max-w-prose text-[13px] text-slate-600 dark:text-slate-400">
            {afterResumeSentence(stopped, domain.WarmupLastStep, warmup.RateLimits)}
          </p>
        </div>

        <div className="pt-3">
          <LastThreeDays rates={warmup.LastThreeDays} limits={warmup.RateLimits} explainStop />
        </div>
      </section>
    </div>
  )
}

function StoppedRow({ tag, title, children, action }: {
  tag: ComponentChildren
  title: string
  children: ComponentChildren
  action?: ComponentChildren
}) {
  return (
    <li className="grid gap-x-4 gap-y-1 px-4 py-3.5 sm:grid-cols-[152px_minmax(0,1fr)_auto]">
      <span>{tag}</span>
      <div>
        <p className="text-sm font-medium text-slate-900 dark:text-white">{title}</p>
        <div className="max-w-prose text-[13px] text-slate-600 dark:text-slate-400">{children}</div>
      </div>
      {action ?? <span />}
    </li>
  )
}

/** The held broadcast emails, each broadcast linked to its own page. */
function HeldRow({ clientCompanyId, stopped }: { clientCompanyId: string; stopped: SendingDomainStoppedDto }) {
  const held = stopped.HeldEmails
  const title = held === 0 ? 'No broadcast emails waiting' : broadcastEmailCount(held)
  const broadcasts = stopped.HeldBroadcasts
  const single = broadcasts.length === 1 ? broadcasts[0] : null

  return (
    <StoppedRow
      tag={<Chip tone="amber">Held</Chip>}
      title={title}
      action={single ? <BroadcastLink clientCompanyId={clientCompanyId} broadcast={single} /> : undefined}
    >
      {broadcasts.length > 1 ? (
        <ul className="space-y-1">
          {broadcasts.map(broadcast => (
            <li key={broadcast.BlastId} className="flex flex-wrap items-baseline justify-between gap-x-4">
              <span>{heldBroadcastSource(broadcast)}</span>
              <BroadcastLink clientCompanyId={clientCompanyId} broadcast={broadcast} />
            </li>
          ))}
        </ul>
      ) : single && <span>{heldBroadcastSource(single)} </span>}
      {held > 0 && <span>{heldGoOutSentence(stopped.ResumeDailyLimit)}</span>}
    </StoppedRow>
  )
}

function BroadcastLink({ clientCompanyId, broadcast }: { clientCompanyId: string; broadcast: SendingDomainHeldBroadcastDto }) {
  const href = routes.scoped(clientCompanyId, broadcast.WorkspaceId).webBroadcast(broadcast.CampaignId, broadcast.WebId, broadcast.BlastId)
  return (
    <a href={href} className={`whitespace-nowrap text-sm ${TEXT_LINK}`}>
      View broadcast<span className="sr-only">: {broadcast.Name}</span>
    </a>
  )
}
